import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Star, ArrowRight } from 'lucide-react';
// Add Meta Pixel type definition to avoid TS errors
declare global {
  interface Window {
    fbq: any;
  }
}
export function ComboSection() {
  // Initialize Meta Pixel
  useEffect(() => {
    if (typeof window !== 'undefined' && window.fbq) {
      // Assuming fbq is already loaded in index.html or via a script tag
      // If not, we would need to inject the script here, but usually it's in head
      // For now, we just track the view
      window.fbq('track', 'PageView');
    }
  }, []);
  const handleBuy = (comboName: string, price: number) => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: comboName,
        value: price,
        currency: 'ARS'
      });
    }
  };
  return <section className="py-24 bg-[#0f0a2e] relative" id="pricing" data-id="element-40">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-41" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-42">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-43">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white" data-id="element-44">
            Elige Tu Nivel de Formación
          </h2>
          <p className="text-xl text-purple-200 font-light" data-id="element-45">
            Tres opciones diseñadas para diferentes momentos de tu camino
          </p>
        </div>

        {/* MercadoPago Trust Bar — BEFORE checkout */}
        <div className="max-w-5xl mx-auto mb-4 flex flex-col sm:flex-row items-center justify-center gap-3 bg-white/5 border border-purple-800/40 rounded-lg px-6 py-3" data-id="element-46">
          <span className="text-purple-200 text-sm font-medium" data-id="element-47">
            🔒 Pago seguro procesado por
          </span>
          <img src="https://cdn.magicpatterns.com/uploads/4M9JFC71yGYDRTK2pVZcnK/pasted-image.png" alt="MercadoPago" className="h-8 object-contain" data-id="element-48" />
        </div>

        {/* Urgency Bar */}
        <div className="max-w-5xl mx-auto bg-[#d4af37] text-[#0f0a2e] text-center py-3 font-bold uppercase tracking-wide text-sm sm:text-base rounded-t-lg shadow-lg relative z-20 mb-0" data-id="element-49">
          ⚡ Oferta de Lanzamiento — Precio especial por tiempo limitado
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-start mt-0" data-id="element-50">
          {/* Combo 1 */}
          <motion.div className="bg-[#1e1254] border border-purple-800 rounded-b-lg p-8 flex flex-col h-full relative" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-51">
            {/* Discount Badge */}
            <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg shadow-md z-10" data-id="element-52">
              -43% OFF
            </div>

            {/* Product Image */}
            <div className="flex justify-center mb-6" data-id="element-53">
              <div className="relative" data-id="element-54">
                <div className="absolute inset-0 bg-violet-500 blur-2xl opacity-20 rounded-full" data-id="element-55" />
                <img src="https://cdn.magicpatterns.com/uploads/2LpSGmgWzAztZZHhaMT6RV/20260219_2030_Image_Generation_simple_compose_01khw3svy6f3srpey11zp1m4j5.png" alt="Manual Nivel I" className="relative w-36 shadow-xl rounded-sm" data-id="element-56" />
              </div>
            </div>

            <h3 className="text-2xl font-bold text-white mb-1 font-heading" data-id="element-57">
              Combo 1
            </h3>
            <p className="text-purple-300 text-sm mb-2 uppercase tracking-wider" data-id="element-58">
              Iniciación
            </p>
            <p className="text-purple-200 text-xs mb-6 font-light leading-relaxed" data-id="element-59">
              📘 Formación fundamental en Reiki con bases energéticas y práctica
              guiada.
            </p>

            <div className="mb-8" data-id="element-60">
              <span className="text-gray-400 line-through text-lg block" data-id="element-61">
                $34.990 ARS
              </span>
              <span className="text-4xl font-bold text-white" data-id="element-62">$19.990 ARS</span>
            </div>

            <ul className="space-y-4 mb-8 flex-1" data-id="element-63">
              <li className="flex items-start text-gray-200" data-id="element-64">
                <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-65" />
                <div data-id="element-66">
                  <span className="font-medium" data-id="element-67">Manual Nivel I</span>
                  <p className="text-xs text-purple-400 font-light" data-id="element-68">
                    Formación fundamental en Reiki con bases energéticas y
                    práctica guiada.
                  </p>
                </div>
              </li>
              <li className="flex items-center text-gray-500" data-id="element-69">
                <X className="w-5 h-5 mr-3 flex-shrink-0" data-id="element-70" /> Manual Nivel II
              </li>
              <li className="flex items-center text-gray-500" data-id="element-71">
                <X className="w-5 h-5 mr-3 flex-shrink-0" data-id="element-72" /> Manual Nivel III
              </li>
              <li className="flex items-center text-gray-200" data-id="element-73">
                <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" data-id="element-74" />{' '}
                3 Bonus Exclusivos
              </li>
            </ul>

            <a href="https://checkout2.rosetadigital.com/cart/47082289168562:1" target="_blank" rel="noopener noreferrer" onClick={() => handleBuy('Combo 1 - Iniciación', 19990)} className="w-full py-3 border border-purple-500 text-purple-100 hover:bg-purple-900/50 rounded transition-colors font-medium text-center block" data-id="element-75">
              Comenzar con Combo 1
            </a>
            <p className="text-center text-purple-400 text-xs mt-3 flex items-center justify-center gap-1" data-id="element-76">
              📄 PDF digital · 📧 Llega a tu email al instante
            </p>
          </motion.div>

          {/* Combo 2 - RECOMMENDED */}
          <motion.div className="bg-[#1e1254] border-2 border-[#d4af37] rounded-lg p-8 flex flex-col h-full relative transform md:-translate-y-4 shadow-[0_0_40px_rgba(212,175,55,0.15)] z-10" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-77">
            {/* Discount Badge */}
            <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg shadow-md z-20" data-id="element-78">
              -45% OFF
            </div>

            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#d4af37] text-[#0f0a2e] px-4 py-1 rounded-full text-sm font-bold flex items-center shadow-lg whitespace-nowrap z-30" data-id="element-79">
              <Star className="w-3 h-3 mr-1 fill-current" data-id="element-80" /> MÁS ELEGIDO
            </div>

            {/* Product Image */}
            <div className="flex justify-center mb-6 mt-2" data-id="element-81">
              <div className="relative" data-id="element-82">
                <div className="absolute inset-0 bg-[#d4af37] blur-2xl opacity-10 rounded-full" data-id="element-83" />
                <img src="https://cdn.magicpatterns.com/uploads/jL6uJXmj93pSiu3Q3T5LxH/20260219_2035_Image_Generation_simple_compose_01khw42bvbfcfvatmfzn8avdj7.png" alt="Manual Nivel I y II" className="relative w-44 shadow-xl rounded-sm" data-id="element-84" />
              </div>
            </div>

            <h3 className="text-3xl font-bold text-white mb-1 font-heading" data-id="element-85">
              Combo 2
            </h3>
            <p className="text-[#d4af37] text-sm mb-2 uppercase tracking-wider font-bold" data-id="element-86">
              Profesional
            </p>
            <p className="text-purple-200 text-xs mb-6 font-light leading-relaxed" data-id="element-87">
              📘 Profundización profesional con símbolos y trabajo energético
              avanzado.
            </p>

            <div className="mb-8" data-id="element-88">
              <span className="bg-purple-900/50 text-[#d4af37] text-xs px-2 py-1 rounded mb-2 inline-block" data-id="element-89">
                MEJOR VALOR
              </span>
              <span className="text-gray-400 line-through text-lg block" data-id="element-90">
                $54.990 ARS
              </span>
              <span className="text-5xl font-bold text-white" data-id="element-91">$29.990 ARS</span>
            </div>

            <ul className="space-y-4 mb-8 flex-1" data-id="element-92">
              <li className="flex items-start text-white font-medium" data-id="element-93">
                <Check className="w-5 h-5 text-[#d4af37] mr-3 flex-shrink-0 mt-0.5" data-id="element-94" />
                <div data-id="element-95">
                  <span data-id="element-96">Manual Nivel I</span>
                  <p className="text-xs text-purple-300 font-light" data-id="element-97">
                    Formación fundamental en Reiki con bases energéticas y
                    práctica guiada.
                  </p>
                </div>
              </li>
              <li className="flex items-start text-white font-medium" data-id="element-98">
                <Check className="w-5 h-5 text-[#d4af37] mr-3 flex-shrink-0 mt-0.5" data-id="element-99" />
                <div data-id="element-100">
                  <span data-id="element-101">Manual Nivel II</span>
                  <p className="text-xs text-purple-300 font-light" data-id="element-102">
                    Profundización profesional con símbolos y trabajo energético
                    avanzado.
                  </p>
                </div>
              </li>
              <li className="flex items-center text-gray-500" data-id="element-103">
                <X className="w-5 h-5 mr-3 flex-shrink-0" data-id="element-104" /> Manual Nivel III
              </li>
              <li className="flex items-center text-white font-medium" data-id="element-105">
                <Check className="w-5 h-5 text-[#d4af37] mr-3 flex-shrink-0" data-id="element-106" />{' '}
                3 Bonus Exclusivos
              </li>
            </ul>

            <a href="https://checkout2.rosetadigital.com/cart/47082293100722:1" target="_blank" rel="noopener noreferrer" onClick={() => handleBuy('Combo 2 - Profesional', 29990)} className="w-full py-4 bg-[#d4af37] hover:bg-[#f0c040] text-[#0f0a2e] font-bold rounded shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] flex justify-center items-center gap-2" data-id="element-107">
              QUIERO EL COMBO 2 <ArrowRight className="w-5 h-5" data-id="element-108" />
            </a>
            <p className="text-center text-purple-300 text-xs mt-3" data-id="element-109">
              La elección de la mayoría de nuestros estudiantes
            </p>
            <p className="text-center text-purple-400 text-xs mt-1 flex items-center justify-center gap-1" data-id="element-110">
              📄 PDF digital · 📧 Llega a tu email al instante
            </p>
          </motion.div>

          {/* Combo 3 */}
          <motion.div className="bg-[#1e1254] border border-purple-800 rounded-b-lg p-8 flex flex-col h-full relative" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} data-id="element-111">
            {/* Discount Badge */}
            <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg shadow-md z-10" data-id="element-112">
              -47% OFF
            </div>

            {/* Product Image */}
            <div className="flex justify-center mb-6" data-id="element-113">
              <div className="relative" data-id="element-114">
                <div className="absolute inset-0 bg-violet-500 blur-2xl opacity-20 rounded-full" data-id="element-115" />
                <img src="https://cdn.magicpatterns.com/uploads/uWJcKpgZZK8NzpBXgvPs3U/20260219_2037_Image_Generation_simple_compose_01khw465j6e6b8gnhjx0grb1w7.png" alt="Manual Nivel I, II y III" className="relative w-44 shadow-xl rounded-sm" data-id="element-116" />
              </div>
            </div>

            <h3 className="text-2xl font-bold text-white mb-1 font-heading" data-id="element-117">
              Combo 3
            </h3>
            <p className="text-purple-300 text-sm mb-2 uppercase tracking-wider" data-id="element-118">
              Maestría
            </p>
            <p className="text-purple-200 text-xs mb-6 font-light leading-relaxed" data-id="element-119">
              📘 Nivel de maestría, liderazgo terapéutico y expansión
              energética.
            </p>

            <div className="mb-8" data-id="element-120">
              <span className="text-gray-400 line-through text-lg block" data-id="element-121">
                $74.990 ARS
              </span>
              <span className="text-4xl font-bold text-white" data-id="element-122">$39.990 ARS</span>
            </div>

            <ul className="space-y-4 mb-8 flex-1" data-id="element-123">
              <li className="flex items-start text-gray-200" data-id="element-124">
                <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-125" />
                <div data-id="element-126">
                  <span className="font-medium" data-id="element-127">Manual Nivel I</span>
                  <p className="text-xs text-purple-400 font-light" data-id="element-128">
                    Formación fundamental en Reiki con bases energéticas y
                    práctica guiada.
                  </p>
                </div>
              </li>
              <li className="flex items-start text-gray-200" data-id="element-129">
                <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-130" />
                <div data-id="element-131">
                  <span className="font-medium" data-id="element-132">Manual Nivel II</span>
                  <p className="text-xs text-purple-400 font-light" data-id="element-133">
                    Profundización profesional con símbolos y trabajo energético
                    avanzado.
                  </p>
                </div>
              </li>
              <li className="flex items-start text-gray-200" data-id="element-134">
                <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-135" />
                <div data-id="element-136">
                  <span className="font-medium" data-id="element-137">Manual Nivel III</span>
                  <p className="text-xs text-purple-400 font-light" data-id="element-138">
                    Nivel de maestría, liderazgo terapéutico y expansión
                    energética.
                  </p>
                </div>
              </li>
              <li className="flex items-center text-gray-200" data-id="element-139">
                <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" data-id="element-140" />{' '}
                3 Bonus Exclusivos
              </li>
            </ul>

            <a href="https://checkout2.rosetadigital.com/cart/47082294018226:1" target="_blank" rel="noopener noreferrer" onClick={() => handleBuy('Combo 3 - Maestría', 39990)} className="w-full py-3 border border-purple-500 text-purple-100 hover:bg-purple-900/50 rounded transition-colors font-medium text-center block" data-id="element-141">
              Acceder a Maestría Completa
            </a>
            <p className="text-center text-purple-400 text-xs mt-3 flex items-center justify-center gap-1" data-id="element-142">
              📄 PDF digital · 📧 Llega a tu email al instante
            </p>
          </motion.div>
        </div>

        {/* MercadoPago Trust Bar — AFTER checkout */}
        <div className="max-w-5xl mx-auto mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 bg-white/5 border border-purple-800/40 rounded-lg px-6 py-3" data-id="element-143">
          <span className="text-purple-200 text-sm font-medium" data-id="element-144">
            🔒 Pago seguro procesado por
          </span>
          <img src="https://cdn.magicpatterns.com/uploads/4M9JFC71yGYDRTK2pVZcnK/pasted-image.png" alt="MercadoPago" className="h-8 object-contain" data-id="element-145" />
        </div>

        {/* Bonus Showcase */}
        <div className="mt-20 max-w-5xl mx-auto" data-id="element-146">
          <div className="text-center mb-8" data-id="element-147">
            <span className="bg-purple-800/50 text-purple-200 px-4 py-1 rounded-full text-sm font-semibold border border-purple-600" data-id="element-148">
              GRATIS CON CUALQUIER COMBO
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-149">
            {[{
            title: 'Guía Esencial de los Chakras',
            subtitle: 'Sistema completo de chakras explicado de forma práctica y estructurada.',
            img: 'https://cdn.magicpatterns.com/uploads/iT6CAjpZxzuLT4GuyCD8zZ/20260219_2003_Image_Generation_simple_compose_01khw27er6etw9jp4f5fxpvbws.png'
          }, {
            title: 'Guía de Protección Energética',
            subtitle: 'Protocolos profesionales para proteger y limpiar tu energía.',
            img: 'https://cdn.magicpatterns.com/uploads/ey22RMkHZWf2kLzjVZ2Pcm/20260219_2004_Image_Generation_simple_compose_01khw2agsbf9gvt6rknbkheg31.png'
          }, {
            title: 'Colección de Meditaciones',
            subtitle: 'Meditaciones guiadas para potenciar práctica y desarrollo espiritual.',
            img: 'https://cdn.magicpatterns.com/uploads/abya1u3NRhNhNLGoB63SpW/20260219_2006_Image_Generation_simple_compose_01khw2em1nfzgbmz1svjqvkt53.png'
          }].map((bonus, i) => <div key={i} className="flex flex-col items-center text-center group" data-id="element-150">
                <div className="relative mb-4 transition-transform duration-300 transform group-hover:-translate-y-2" data-id="element-151">
                  <div className="absolute inset-0 bg-violet-500 blur-xl opacity-20 rounded-full" data-id="element-152" />
                  <img src={bonus.img} alt={bonus.title} className="relative w-32 md:w-40 shadow-lg rounded-sm" data-id="element-153" />
                </div>
                <h4 className="text-white font-medium text-lg font-heading mb-1" data-id="element-154">
                  {bonus.title}
                </h4>
                <p className="text-purple-300 text-xs font-light mb-1 px-2" data-id="element-155">
                  {bonus.subtitle}
                </p>
                <p className="text-purple-400 text-sm" data-id="element-156">
                  Valorado en $9.900 ARS
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}